import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  Grid,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ApiKeyCard } from '../components/ApiKeyCard';
import { IntroductionCard } from '../components/IntroductionCard';
import { getEndpointSlug } from '../utils/docs';
import { useDeveloperInfo } from '../hooks/useApi';
import { config } from '../config';

export function DashboardPage() {
  const { data, isLoading, error } = useDeveloperInfo();

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert severity="error">
          Failed to load developer information. Please try again later.
        </Alert>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <Alert severity="warning">No data available.</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <IntroductionCard />
        </Grid>

        <Grid item xs={12}>
          <ApiKeyCard
            apiKey={data.user.apiKey}
            email={data.user.email}
            userId={data.user.userId}
            enabled={data.user.enabled}
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Endpoints
              </Typography>

              {(data.endpoints ?? []).length === 0 ? (
                <Alert severity="info">
                  No endpoints are currently available for your account.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {(data.endpoints ?? []).map((endpoint, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={2}
                        >
                          <Box flex={1}>
                            <Typography
                              variant="subtitle1"
                              component="div"
                              gutterBottom
                            >
                              {endpoint.name || endpoint.prefix}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="primary"
                              fontFamily="monospace"
                              display="block"
                              sx={{ mb: 1 }}
                            >
                              {config.apiUrl}{endpoint.prefix}
                            </Typography>
                            {endpoint.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {endpoint.description}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {(endpoint.methods ?? []).map((method) => (
                                <Chip
                                  key={method}
                                  label={method}
                                  size="small"
                                  color={
                                    method === 'GET'
                                      ? 'success'
                                      : method === 'POST'
                                      ? 'primary'
                                      : method === 'DELETE'
                                      ? 'error'
                                      : 'default'
                                  }
                                />
                              ))}
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              component={Link}
                              to={`/docs/${getEndpointSlug(endpoint.docsUrl ?? endpoint.documentation_url ?? '')}`}
                              endIcon={<OpenInNew />}
                            >
                              View Docs
                            </Button>
                            {endpoint.notebookUrl && (
                              <Button
                                variant="outlined"
                                component="a"
                                href={endpoint.notebookUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<OpenInNew />}
                              >
                                Notebook
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
}
